import {
    FunctionComponent,
    useDeferredValue,
    useEffect,
    useMemo,
    useState,
} from "react"
import { UsersAdminTableDataI } from "./types"
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getFacetedUniqueValues,
    ColumnFiltersState,
    SortingState,
    Table,
} from "@tanstack/react-table"
import usersAdminColumns from "./column"
import { Outlet, useLocation, useSearchParams } from "react-router-dom"
import styles from "./table.module.scss"
import UsersAdminHeader from "./header"
import UsersAdminActionHeader from "./actionHeader"
import classNames from "classnames"
import {
    getUsersAdminStateFromUrlQueryParams,
    usersAdminPageSizeSelectorArray,
} from "./tableUtils"
import TanStackPaginator from "../shared/tanStack/paginator"
import { tableStateToUrlSearchParams } from "../table/tableUtil"
import { TableStateType } from "../table/tableTypes"
import { STATUS_COLUMN_ID } from "./column"

interface UsersAdminTableI {
    data: UsersAdminTableDataI[]
}

const UsersAdminTable: FunctionComponent<UsersAdminTableI> = ({ data }) => {
    // hooks
    const [searchParams, setSearchParams] = useSearchParams()
    const location = useLocation()

    // state
    // get the initial state from the URL
    const initTableState: TableStateType = useMemo(() => {
        const urlState: TableStateType =
            getUsersAdminStateFromUrlQueryParams(searchParams)

        // we want the active column to be checked by default - for all requester roles
        return {
            ...urlState,
            filter: [...urlState.filter, { id: STATUS_COLUMN_ID, value: true }],
        }
    }, [data])

    const [globalFilter, setGlobalFilter] = useState(initTableState.search)
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        initTableState.filter
    )
    const [sortingState, setSortingState] = useState<SortingState>(
        initTableState.sorting
    )
    const [paginationState, setPaginationState] = useState({
        pageIndex: initTableState.pageIndex,
        pageSize: initTableState.size,
    })

    const deferredGlobalFilter = useDeferredValue(globalFilter)
    const deferredColumnFilters = useDeferredValue(columnFilters)
    const deferredSortingState = useDeferredValue(sortingState)
    const isStateDeferred =
        deferredGlobalFilter !== globalFilter ||
        deferredColumnFilters !== columnFilters ||
        deferredSortingState !== sortingState

    const table = useReactTable({
        data: data,
        // autoResetPageIndex is because false : if true, the page index will be reset to 0 whenever the data changes (from a mutation).
        // That creates a bad user experience, in this circumstance if the user changed a run mode on page 5 they would be directed back to page 1.
        // The down side of autoResetPageIndex: false is that we need to manually reset the page index when updating filters, globals and sorting
        autoResetPageIndex: false,
        columns: usersAdminColumns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        onColumnFiltersChange: (updater) => {
            table.setPageIndex(0)
            return setColumnFilters(updater)
        },
        onSortingChange: (updater) => {
            table.setPageIndex(0)
            return setSortingState(updater)
        },
        onGlobalFilterChange: (updater) => {
            table.setPageIndex(0)
            return setGlobalFilter(updater)
        },
        onPaginationChange: (updater) => {
            return setPaginationState(updater)
        },
        state: {
            globalFilter: deferredGlobalFilter,
            columnFilters: deferredColumnFilters,
            sorting: sortingState,
            pagination: paginationState,
        },
    })

    useEffect(() => {
        if (!location.pathname.endsWith("/admin/users")) {
            return
        }

        const newParams = tableStateToUrlSearchParams(
            paginationState,
            sortingState,
            columnFilters,
            globalFilter
        )

        if (newParams.get(STATUS_COLUMN_ID)) {
            // delete userStatus from columnFilter if it exists. For the time being we will not map userStatus app state to url state or vice versa
            // This is for simplicity because org admin can only fetch active users
            newParams.delete(STATUS_COLUMN_ID)
        }

        setSearchParams(newParams, { replace: true })
        // having data as a dependency allows the resynchronization of app state & url state after mutations
    }, [columnFilters, sortingState, globalFilter, paginationState, data])

    return (
        <>
            <Outlet />
            <div className={styles.tableContainer}>
                <UsersAdminActionHeader
                    globalFilter={globalFilter}
                    table={table}
                />
                <div className={styles.overflowContainer}>
                    <table className={styles.tableClass}>
                        <UsersAdminHeader table={table} />
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    className={classNames(
                                        styles.tableRow,
                                        isStateDeferred && styles.deferredRow
                                    )}
                                    key={row.id}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            className={classNames(
                                                styles.tableCell,
                                                styles[`${cell.column.id}_Cell`]
                                            )}
                                            key={cell.id}
                                        >
                                            <span className={styles.cellSpan}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </span>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <TanStackPaginator
                    pageSizeOptions={usersAdminPageSizeSelectorArray}
                    tableInstance={table as Table<unknown>}
                />
            </div>
        </>
    )
}

export default UsersAdminTable
